import { COMMANDAPI, QUERYAPI } from '../../../constants/global';
import type {
	IIrevAnalyticsByElectIonResponse,
	IIrevAnalyticsChartResponse,
	IIrevAnalyticsResponse,
	IIrevAnalyticsStatsRequestArg,
	IIrevAnalyticsValidatePartiesArg,
	IIrevElectionSubmissionResponse,
	IIRevResultState,
	IIRevResultStates,
	IUploadIrevState,
	IZipRecruiterStates,
} from '../../../interface/state/IRev';
import { irevType } from '../../types';
import type { IGetQuery, IResponse } from '../api-utils';
import * as utils from '../utils';

export interface IIrevDataModel {
	Upload: string;
	Data: {
		Id: string;
		Election: string;
		PoolingUnit: string;
		PoolingUnitCode: string;
		Location: {
			Address: string;
			Latitude: string;
			Longitude: string;
		};
		Ward: string;
		LocalGovernment: string;
		State: string;
		GeoZone: string;
		Zone: string;
		PresidingOfficer: {
			Id: string;
			Name: string;
		};
		VotersOnRegister: number;
		AccreditedVoters: number;
		BallotPapersIssuedToPoolingUnit: number;
		UnusedBallotPapers: number;
		RejectedBallot: number;
		TotalValidVotes: number;
		TotalUsedBallotPapers: number;
		Status: string;
		DocumentUrl: string;
		CreateAt: string;
		ApprovedBy: string;
		ApprovedAt: string;
		Results: [];
	};
}

export const upload_IRevResult = ({
	data,
	onFailure,
	onSuccess,
}: IResponse<IUploadIrevState> & {
	data: any;
}) =>
	utils.httpUploadMediaMethod({
		apiData: {
			url: '',
			customurl: `${COMMANDAPI}/api/v1/i-elect/Upload/result`,
			header: utils.headerMultipart(),
			data,
		},
		actionType: irevType.upload_IRevResult,
		onFailure,
		onSuccess,
	});

export interface IWardResultMap {
	pollingUnitId: number;
	partyId: number;
	votes: number;
}

// ward
export interface ICreateWardResult {
	documentUrls: string[];
	election: {
		id: number;
		name: string;
	};
	ward: {
		code: string;
		name: string;
	};
	localGovernment: {
		code: string;
		name: string;
	};
	state: {
		code: string;
		name: string;
	};
	geoZone: {
		code: string;
		name: string;
	};
	zone: {
		code: string;
		name: string;
	};
	presidingOfficer: {
		id: string;
		name: string;
	};
	votersOnRegister: number;
	accreditedVoters: number;
	ballotPapersIssuedToPoolingUnit: number;
	unusedBallotPapers: number;
	rejectedBallot: number;
	totalValidVotes: number;
	totalUsedBallotPapers: number;
	results: {
		partyId: number;
		partyName: string;
		votes: number;
	}[];
	createdBy: {
		id: number;
		name: string;
	};
	flags: string[];
	resultMap: IWardResultMap[];
}

export const push_IRevWardDataModel = ({
	data,
	onFailure,
	onSuccess,
}: IResponse<IIrevElectionSubmissionResponse> & {
	data: ICreateWardResult;
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${COMMANDAPI}/api/v1/i-elect/WardResult/create-ward-result`,
			header: utils.header(),
			data,
		},
		actionType: irevType.push_IRevWardDataModel,
		onFailure,
		onSuccess,
	});

export interface IRrevDataReq {
	pageNumber?: number;
	pageSize?: number;
	sortBy?: string;
	elections?: string[];
	states?: string[];
	geoZones?: string[];
	pollingUnits?: string[];
	zones?: string[];
	localGovernments?: string[];
	wards?: string[];
	orderBy?: string;
	isDateFilterOn?: string;
	isBoolFilterOn?: string;
	minCreatedDate?: string;
	maxCreatedDate?: string;
	minUpdatedDate?: string;
	maxUpdatedDate?: string;
	minDeletedDate?: string;
	maxDeletedDate?: string;
	// new fields
	electionId?: string | number;
	electionName?: string[];
	pollingUnitCode?: string[];
	pollingUnitName?: string[];
	wardCode?: string[];
	wardName?: string[];
	localGovernmentCode?: string[];
	localGovernmentName?: string[];
	stateCode?: string[];
	stateName?: string[];
}

export const get_IRevPollingUnitDataModel = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery<IIRevResultStates> & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/PollingUnitResult/get-all-multi-paged-by-code`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevPollingUnitDataModel,
		onSuccess,
		onFailure,
	});

export const get_IRevPollingUnitDataModelByID = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery<IIRevResultStates> & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/PollingUnitResult/get-all-multi-paged-by-code`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevPollingUnitDataModelByID,
		onSuccess,
		onFailure,
	});

export const get_IRevWardDataModel = ({ onFailure, onSuccess, data }: IGetQuery<IIRevResultStates> & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/WardResult/get-all-multi-paged`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevWardDataModel,
		onSuccess,
		onFailure,
	});

export const get_IRevWardDataModelByID = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery<IIRevResultStates> & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/WardResult/get-all-multi-paged`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevWardDataModelByID,
		onSuccess,
		onFailure,
	});

// state

export const get_IRevStateDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/StateResult/get-all-multi-paged`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevStateDataModel,
		onSuccess,
		onFailure,
	});

export const get_IRevStateDataModelByID = ({ onFailure, onSuccess, data }: IGetQuery & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/StateResult/get-all-multi-paged`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevStateDataModelByID,
		onSuccess,
		onFailure,
	});

export interface IStateResultMap {
	lgaId: number;
	partyId: number;
	votes: number;
}

export interface ICreateStateResult {
	documentUrls: string[];
	election: {
		id: number;
		name: string;
	};
	localGovernment: {
		code: string;
		name: string;
	};
	state: {
		code: string;
		name: string;
	};
	geoZone: {
		code: string;
		name: string;
	};
	zone: {
		code: string;
		name: string;
	};
	presidingOfficer: {
		id: string;
		name: string;
	};
	votersOnRegister: number;
	accreditedVoters: number;
	ballotPapersIssuedToPoolingUnit: number;
	unusedBallotPapers: number;
	rejectedBallot: number;
	totalValidVotes: number;
	totalUsedBallotPapers: number;
	results: {
		partyId: number;
		partyName: string;
		votes: number;
	}[];
	createdBy: {
		id: number;
		name: string;
	};
	flags: string[];
	resultMap: IStateResultMap[];
}

export const push_IRevStateDataModel = ({
	data,
	onFailure,
	onSuccess,
}: IResponse<IIrevElectionSubmissionResponse> & {
	data: ICreateStateResult;
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${COMMANDAPI}/api/v1/i-elect/StateResult/create-state-result`,
			header: utils.header(),
			data,
		},
		actionType: irevType.push_IRevStateDataModel,
		onFailure,
		onSuccess,
	});

// lga

export interface IResult {
	partyId: number;
	partyName: string;
	votes: number;
}

export interface ILGAResultMap {
	wardId: number;
	partyId: number;
	votes: number;
}

export interface ICreateLGAResult {
	documentUrls: string[];
	election: {
		id: number;
		name: string;
	};
	localGovernment: {
		code: string;
		name: string;
	};
	state: {
		code: string;
		name: string;
	};
	geoZone: {
		code: string;
		name: string;
	};
	zone: {
		code: string;
		name: string;
	};
	presidingOfficer: {
		id: string;
		name: string;
	};
	votersOnRegister: number;
	accreditedVoters: number;
	ballotPapersIssuedToPoolingUnit: number;
	unusedBallotPapers: number;
	rejectedBallot: number;
	totalValidVotes: number;
	totalUsedBallotPapers: number;
	results: IResult[];
	createdBy: {
		id: number;
		name: string;
	};
	resultMap: ILGAResultMap[];
}

export const push_IRevLGADataModel = ({
	data,
	onFailure,
	onSuccess,
}: IResponse<IIrevElectionSubmissionResponse> & {
	data: ICreateLGAResult;
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${COMMANDAPI}/api/v1/i-elect/LocalGovernmentResult/create-local_government-result`,
			header: utils.header(),
			data,
		},
		actionType: irevType.push_IRevLGADataModel,
		onFailure,
		onSuccess,
	});

export const get_IRevLGADataModel = ({ onFailure, onSuccess, data }: IGetQuery<IIRevResultStates> & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/LocalGovernmentResult/get-all-multi-paged`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevLGADataModel,
		onSuccess,
		onFailure,
	});

export const get_IRevLGADataModelByID = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery<IIRevResultStates> & { data?: IRrevDataReq }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/LocalGovernmentResult/get-all-multi-paged`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevLGADataModelByID,
		onSuccess,
		onFailure,
	});

// polling unit

export interface ICreatePUResult {
	documentUrls: string[];
	election: {
		id: number;
		name: string;
	};
	poolingUnitCode: string;
	presidingOfficer: {
		id: string;
		name: string;
	};
	votersOnRegister: number;
	accreditedVoters: number;
	ballotPapersIssuedToPoolingUnit: number;
	unusedBallotPapers: number;
	rejectedBallot: number;
	totalValidVotes: number;
	totalUsedBallotPapers: number;
	results: {
		partyId: number;
		partyName: string;
		votes: number;
	}[];
	createdBy: {
		id: number;
		name: string;
	};
	flags: string[];
	// ward: {
	//   code: string
	//   name: string
	// }
	// localGovernment: {
	//   code: string
	//   name: string
	// }
	// state: {
	//   code: string
	//   name: string
	// }
	// geoZone: {
	//   code: string
	//   name: string
	// }
	// zone: {
	//   code: string
	//   name: string
	// }
}

export const push_IRevPollingUnitDataModel = ({
	data,
	onFailure,
	onSuccess,
}: IResponse<IIrevElectionSubmissionResponse> & {
	data: ICreatePUResult;
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${COMMANDAPI}/api/v1/i-elect/PollingUnitResult/create-polling-unit-result`,
			header: utils.header(),
			data,
		},
		actionType: irevType.push_IRevPollingUnitDataModel,
		onFailure,
		onSuccess,
	});

export interface ICreatePUExcelResult {
	election: {
		id: number;
		name: string;
	};
	poolingUnitCode: string;
	documentUrl: string;
}

export const push_IRevPollingUnitDataExcelResult = ({
	data,
	onFailure,
	onSuccess,
}: IResponse<IIrevElectionSubmissionResponse> & {
	data: ICreatePUExcelResult;
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${COMMANDAPI}/api/v1/i-elect/PollingUnitResult/create-polling-unit-result-from-excel`,
			header: utils.header(),
			data,
		},
		actionType: irevType.push_IRevPollingUnitDataExcelResult,
		onFailure,
		onSuccess,
	});

export interface IAddNotes {
	id: string;
	notes: [
		{
			message: string;
			createdBy: {
				id: number;
				name: string;
			};
			createdAt: string;
		},
	];
	appUser: {
		id: number;
		name: string;
	};
}

export interface IDeleteRequest {
	id: string;
	/** What the command DTO (`…ResultDeleteRequestDto`) actually binds — `{ Id, DeleteRequestedBy }`. */
	deleteRequestedBy?: {
		id: number;
		name: string;
	};
	/** Ignored by the server; kept while older builds may still send it. */
	approvedBy: {
		id: number;
		name: string;
	};
	isApproved: boolean;
}

export interface IApproveDeleteRequest {
	id: string;
	approvedBy: {
		id: number;
		name: string;
	};
	isApproved: boolean;
}

export const addNote_IRevPollingUnitDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IAddNotes }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/PollingUnitResult/add-notes`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.addNote_IRevPollingUnitDataModel,
		onSuccess,
		onFailure,
	});

export const deleteRequest_IRevPollingUnitDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/PollingUnitResult/delete-pollingUnit-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.deleteRequest_IRevPollingUnitDataModel,
		onSuccess,
		onFailure,
	});

export const approveDeleteRequest_IRevPollingUnitDataModel = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery & { data: IApproveDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/PollingUnitResult/approve-delete-pollingUnit-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.approveDeleteRequest_IRevPollingUnitDataModel,
		onSuccess,
		onFailure,
	});

export const addNote_IRevWardDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IAddNotes }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/WardResult/add-notes`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.addNote_IRevWardDataModel,
		onSuccess,
		onFailure,
	});

export const deleteRequest_IRevWardDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/WardResult/delete-ward-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.deleteRequest_IRevWardDataModel,
		onSuccess,
		onFailure,
	});

export const approveDeleteRequest_IRevWardDataModel = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery & { data: IApproveDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/WardResult/approve-delete-ward-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.approveDeleteRequest_IRevWardDataModel,
		onSuccess,
		onFailure,
	});

export const addNote_IRevLGADataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IAddNotes }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/LocalGovernmentResult/add-notes`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.addNote_IRevLGADataModel,
		onSuccess,
		onFailure,
	});

export const deleteRequest_IRevLGADataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/LocalGovernmentResult/delete-local_government-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.deleteRequest_IRevLGADataModel,
		onSuccess,
		onFailure,
	});

export const approveDeleteRequest_IRevLGADataModel = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery & { data: IApproveDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/LocalGovernmentResult/approve-delete-local_government-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.approveDeleteRequest_IRevLGADataModel,
		onSuccess,
		onFailure,
	});

export const addNote_IRevStateDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IAddNotes }) =>
	utils.httpPostMethod({
		apiData: {
			customurl: `${COMMANDAPI}/api/v1/i-elect/StateResult/add-notes`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.addNote_IRevStateDataModel,
		onSuccess,
		onFailure,
	});

export const deleteRequest_IRevStateDataModel = ({ onFailure, onSuccess, data }: IGetQuery & { data: IDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			// NOTE: StateResultController really does name this route `delete-ward-result-request`
			// (api.command/Controllers/IElect/StateResultController.cs:204). Keep the misnamed
			// segment — "correcting" it to `-state-` 404s.
			customurl: `${COMMANDAPI}/api/v1/i-elect/StateResult/delete-ward-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.deleteRequest_IRevStateDataModel,
		onSuccess,
		onFailure,
	});

export const approveDeleteRequest_IRevStateDataModel = ({
	onFailure,
	onSuccess,
	data,
}: IGetQuery & { data: IApproveDeleteRequest }) =>
	utils.httpPostMethod({
		apiData: {
			// NOTE: as above — StateResultController.cs:254 names this `approve-delete-ward-result-request`.
			customurl: `${COMMANDAPI}/api/v1/i-elect/StateResult/approve-delete-ward-result-request`,
			url: '',
			header: utils.header(),
			data,
		},
		actionType: irevType.approveDeleteRequest_IRevStateDataModel,
		onSuccess,
		onFailure,
	});

export interface IRequestLogger {
	referenceId: string[];
	elections: string[];
	poolingUnits: string[];
	wards: string[];
	localGovernments: string[];
	states: string[];
	geoZones: string[];
	zones: string[];
	presidinOfficerIds: string[];
	names: string[];
	status: string[];
}

// result download request
export const get_IRevDownloadRequest = ({ onFailure, onSuccess }: IGetQuery<IZipRecruiterStates>) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/ZipResultRequest/get-all-paged`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevDownloadRequest,
		onSuccess,
		onFailure,
	});

// by reference id
export const get_IRevPollingUnitByRefId = ({ onFailure, onSuccess, refId }: IGetQuery<IIRevResultState> & { refId: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/PollingUnitResult/get-by-referenceid/${refId}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevPollingUnitByRefId,
		onSuccess,
		onFailure,
	});

export const get_IRevPollingUnitById = ({ onFailure, onSuccess, id }: IGetQuery<IIRevResultState> & { id: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/PollingUnitResult/get-by-id/${id}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevPollingUnitById,
		onSuccess,
		onFailure,
	});

export const get_IRevWardByRefId = ({ onFailure, onSuccess, refId }: IGetQuery<IIRevResultState> & { refId: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/WardResult/get-by-referenceid/${refId}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevWardByRefId,
		onSuccess,
		onFailure,
	});

export const get_IRevWardById = ({ onFailure, onSuccess, id }: IGetQuery<IIRevResultState> & { id: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/WardResult/get-by-id/${id}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevWardById,
		onSuccess,
		onFailure,
	});

export const get_IRevLGAByRefId = ({ onFailure, onSuccess, refId }: IGetQuery<IIRevResultState> & { refId: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/LocalGovernmentResult/get-by-referenceid/${refId}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevLGAByRefId,
		onSuccess,
		onFailure,
	});

export const get_IRevLGAById = ({ onFailure, onSuccess, id }: IGetQuery<IIRevResultState> & { id: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/LocalGovernmentResult/get-by-id/${id}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevLGAById,
		onSuccess,
		onFailure,
	});

export const get_IRevStateByRefId = ({ onFailure, onSuccess, refId }: IGetQuery<IIRevResultState> & { refId: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/StateResult/get-by-referenceid/${refId}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevStateByRefId,
		onSuccess,
		onFailure,
	});

export const get_IRevStateById = ({ onFailure, onSuccess, id }: IGetQuery<IIRevResultState> & { id: string }) =>
	utils.httpGetMethod({
		apiData: {
			customurl: `${QUERYAPI}/api/v1/i-elect/StateResult/get-by-id/${id}`,
			url: '',
			header: utils.header(),
		},
		actionType: irevType.get_IRevStateById,
		onSuccess,
		onFailure,
	});

export const get_IRevResultAnalyticsStats = ({
	onFailure,
	onSuccess,
	data,
}: IResponse<IIrevAnalyticsResponse> & { data: IIrevAnalyticsStatsRequestArg }) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${QUERYAPI}/api/v1/i-elect/Result/resultAnalytics`,
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevResultAnalyticsStats,
		onSuccess,
		onFailure,
	});

export const get_IRevResultAnalyticsForElection = ({
	onFailure,
	onSuccess,
	data,
}: IResponse<IIrevAnalyticsByElectIonResponse[]> & { data: IIrevAnalyticsValidatePartiesArg }) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${QUERYAPI}/api/v1/i-elect/Result/validate-all-parties`,
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevResultAnalyticsForElection,
		onSuccess,
		onFailure,
	});

export const get_IRevResultAnalyticsChart = ({
	onFailure,
	onSuccess,
	data,
}: IResponse<IIrevAnalyticsChartResponse[]> & {
	data: {
		resultType?: string;
		resultSource: number;
		electionId: number;
	};
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${QUERYAPI}/api/v1/i-elect/Result/result-chart`,
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevResultAnalyticsChart,
		onSuccess,
		onFailure,
	});

export const get_IRevResultAnalyticsChartCompare = ({
	onFailure,
	onSuccess,
	data,
}: IResponse<IIrevAnalyticsChartResponse[]> & {
	data: {
		resultType?: string;
		resultSource: number;
		electionId: number;
	};
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: `${QUERYAPI}/api/v1/i-elect/Result/result-chart`,
			header: utils.header(),
			data,
		},
		actionType: irevType.get_IRevResultAnalyticsChartCompare,
		onSuccess,
		onFailure,
	});
